import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  UiIcon,
  UiCheckbox,
  UiFormControlLabel,
  UiToolbar,
} from 'ui-components';
import DisplayError from 'components/error/DisplayError';
import Loading from 'components/loading/Loading';
import Pagination from 'components/pagination/Pagination';
import { StyledUiTableRedux } from './UiTableRedux.styles';

function TableHeader({ selectable, columns }) {
  return (
    <TableHead>
      <TableRow>
        {selectable && (
          <TableCell
            align='right'
            padding='checkbox'
            className='checkbox-column'
            sx={{
              width: '24px',
            }}
          ></TableCell>
        )}
        {columns?.map((column) => (
          <TableCell
            key={column.key}
            align='right'
            className={classNames(
              column?.className,
              column.key,
              column.hideInMobile && 'hide-in-mobile'
            )}
            onClick={column.onSort}
            sx={{
              width: column.width,
            }}
          >
            {column.sorting ? (
              <span className='flex gap-4 pointer'>
                {column.title}
                {column.sorting === 'asc' ? (
                  <UiIcon icon='ri-arrow-up-s-line' />
                ) : (
                  <UiIcon icon='ri-arrow-down-s-line' />
                )}
              </span>
            ) : (
              column.title
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

TableHeader.propTypes = {
  columns: PropTypes.array,
  selectable: PropTypes.bool,
};

function TableBodyEmpty({ columns, title, emptyIcon, dataTestid }) {
  const { t } = useTranslation();

  return (
    <TableBody className='empty'>
      <TableRow data-testid={`${dataTestid}-table-empty-row`}>
        <TableCell colSpan={columns?.length}>
          <div className='flex center'>
            {emptyIcon && (
              <UiIcon
                icon={emptyIcon}
                size={25}
                className='mb-18'
                color='#CCD5DC'
                data-testid={`${dataTestid}-empty-icon`}
              />
            )}
          </div>
          <div className='empty-text'>
            {t('common:emptyList', {
              title,
            })}
          </div>
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

TableBodyEmpty.propTypes = {
  columns: PropTypes.array,
  title: PropTypes.string,
  emptyIcon: PropTypes.string,
  dataTestid: PropTypes.string,
};

function TableBodyWithData({
  columns,
  data,
  selectable,
  toggleRowSelection,
  isRowChecked,
  dataTestid,
}) {
  return (
    <TableBody className='with-data'>
      {data.map((item, index) => (
        <TableRow
          key={item.id || index}
          data-testid={`${dataTestid}-table-row`}
        >
          {selectable && (
            <TableCell
              key='checkbox'
              align='right'
              padding='checkbox'
              className='checkbox-column'
            >
              <UiCheckbox
                onChange={(event) => toggleRowSelection(event, item)}
                checked={isRowChecked(item.id)}
                inputProps={{
                  'data-testid': `${dataTestid}-item-checkbox`,
                }}
              />
            </TableCell>
          )}
          {columns.map((column) => (
            <TableCell
              key={column.key}
              align='right'
              className={classNames(
                column?.className,
                column.key,
                column.hideInMobile && 'hide-in-mobile'
              )}
              sx={{
                width: column.width,
              }}
            >
              {typeof column.render === 'function' && column.render(item)}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

TableBodyWithData.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  selectable: PropTypes.bool,
  toggleRowSelection: PropTypes.func,
  isRowChecked: PropTypes.func,
  dataTestid: PropTypes.string,
};

function TableCaption({
  data,
  caption,
  selectedRows,
  selectable,
  toggleAllRowsSelection,
  dataTestid,
}) {
  const { t } = useTranslation();

  return (
    <div className={`ui-table-caption ${selectable ? '' : 'hidden-checkbox'}`}>
      {selectable && (
        <UiFormControlLabel
          label={t('common:selectAll')}
          className='select-all'
          control={
            <UiCheckbox
              indeterminate={
                selectedRows.length > 0 && selectedRows.length < data?.length
              }
              checked={data?.length > 0 && selectedRows.length === data?.length}
              onChange={toggleAllRowsSelection}
              inputProps={{
                'data-testid': `${dataTestid}-table-select-all`,
              }}
            />
          }
        />
      )}
      {caption}
    </div>
  );
}

TableCaption.propTypes = {
  data: PropTypes.array,
  caption: PropTypes.any,
  selectable: PropTypes.bool,
  dataTestid: PropTypes.string,
  selectedRows: PropTypes.array,
  toggleAllRowsSelection: PropTypes.func,
};

export function UiTableRedux({
  data,
  error,
  loading,
  total,
  columns,
  title,
  toolbarTitle,
  emptyIcon,
  currentPage = 1,
  onPaginate,
  caption,
  selectable,
  toolbar,
  dataTestid = '',
  onRowSelectionChange = () => {},
  pageCount,
}) {
  const [selectedRows, setSelectedRows] = useState([]);

  const pageSize = useSelector(
    (state) =>
      state?.settingsReducer?.filters?.public?.pageContentCountSlider
        .selectedValue || 10
  );

  const isRowChecked = useCallback(
    (id) => {
      return selectedRows.some((selectedRow) => selectedRow.id === id);
    },
    [selectedRows]
  );

  const toggleRowSelection = useCallback(
    (event, row) => {
      if (event.target.checked) {
        setSelectedRows((prevSelectedRows) => [...prevSelectedRows, row]);
        onRowSelectionChange([...selectedRows, row]);
        return;
      }
      const filteredRows = selectedRows.filter(
        (selectedRow) => selectedRow.id !== row.id
      );
      setSelectedRows(filteredRows);
      onRowSelectionChange(filteredRows);
    },
    [onRowSelectionChange, selectedRows]
  );

  const toggleAllRowsSelection = useCallback(
    (event) => {
      if (event?.target?.checked) {
        setSelectedRows(data);
        onRowSelectionChange(data);
        return;
      }
      setSelectedRows([]);
      onRowSelectionChange([]);
    },
    [data, onRowSelectionChange]
  );

  if (loading) {
    return (
      <StyledUiTableRedux>
        <TableContainer component={Paper} className='ui-sticky-table loading'>
          <div className='flex center h-70'>
            <Loading spinning={loading} />
          </div>
        </TableContainer>
      </StyledUiTableRedux>
    );
  }

  if (error) {
    return (
      <StyledUiTableRedux>
        <TableContainer component={Paper} className='ui-sticky-table error'>
          <div className='flex center h-70'>
            <DisplayError error={error} />
          </div>
        </TableContainer>
      </StyledUiTableRedux>
    );
  }

  return (
    <StyledUiTableRedux>
      <TableCaption
        data={data}
        caption={caption}
        selectable={selectable}
        selectedRows={selectedRows}
        toggleAllRowsSelection={toggleAllRowsSelection}
        dataTestid={dataTestid}
      />
      <TableContainer component={Paper} className='ui-sticky-table'>
        <Table stickyHeader size={'medium'} data-testid={`${dataTestid}-table`}>
          <TableHeader columns={columns} selectable={selectable} />
          {data?.length === 0 && (
            <TableBodyEmpty
              columns={columns}
              title={title}
              emptyIcon={emptyIcon}
              dataTestid={dataTestid}
            />
          )}
          {data?.length > 0 && (
            <TableBodyWithData
              data={data}
              columns={columns}
              selectable={selectable}
              toggleRowSelection={toggleRowSelection}
              isRowChecked={isRowChecked}
              dataTestid={dataTestid}
            />
          )}
        </Table>
      </TableContainer>
      {data?.length > 0 && total && total > 1 && (
        <Pagination
          total={total}
          currentPage={currentPage}
          onPageChange={typeof onPaginate === 'function' && onPaginate}
          dataTestid={`${dataTestid}-pagination`}
          pageCount={pageCount ?? pageSize}
        />
      )}
      {selectedRows.length > 0 && (
        <UiToolbar
          selected={selectedRows}
          title={toolbarTitle || title}
          actions={toolbar || []}
          updateData={toggleAllRowsSelection}
          onRemoveSelected={toggleAllRowsSelection}
          data-testid={`${dataTestid}-toolbar`}
        />
      )}
    </StyledUiTableRedux>
  );
}

UiTableRedux.propTypes = {
  data: PropTypes.array,
  error: PropTypes.any,
  loading: PropTypes.bool,
  total: PropTypes.number,
  columns: PropTypes.array,
  title: PropTypes.string,
  toolbarTitle: PropTypes.string,
  emptyIcon: PropTypes.string,
  currentPage: PropTypes.number,
  onPaginate: PropTypes.func,
  caption: PropTypes.any,
  selectable: PropTypes.bool,
  toolbar: PropTypes.array,
  onRowSelectionChange: PropTypes.func,
  dataTestid: PropTypes.string,
  pageCount: PropTypes.number,
};
